import React, { useMemo, useState } from 'react'
import {
  AudioPlayer,
  AUDIO_PLAYER_PREVIEW_STATE_LABELS,
  AUDIO_PLAYER_PREVIEW_STATES,
  type AudioPlayerPreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './AudioPlayerShowcase.css'

/** Short public sample for live preview (CORS-friendly MDN demo). */
const DEMO_AUDIO_SRC =
  'https://interactive-examples.mdn.mozilla.net/media/cc0-audio/t-rex-roar.mp3'

const AudioPlayerShowcase: React.FC = () => {
  const [src, setSrc] = useState(DEMO_AUDIO_SRC)
  const [darkMode, setDarkMode] = useState(false)
  const [hideSkip, setHideSkip] = useState(false)
  const [hideVolume, setHideVolume] = useState(false)
  const [hideSpeed, setHideSpeed] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<AudioPlayerPreviewState>('playing')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'audio-player-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="audio-player-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as AudioPlayerPreviewState,
                  )
                }
              >
                {AUDIO_PLAYER_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {AUDIO_PLAYER_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'audio-player-src',
            label: 'Audio URL',
            control: (
              <input
                id="audio-player-src"
                className="ui-field-control"
                type="text"
                value={src}
                onChange={(event) => setSrc(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'Controls',
        fields: [
          {
            id: 'audio-player-hide-skip',
            label: 'Hide skip',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="audio-player-hide-skip"
                  type="checkbox"
                  checked={hideSkip}
                  onChange={(event) => setHideSkip(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'audio-player-hide-volume',
            label: 'Hide volume',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="audio-player-hide-volume"
                  type="checkbox"
                  checked={hideVolume}
                  onChange={(event) => setHideVolume(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'audio-player-hide-speed',
            label: 'Hide speed',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="audio-player-hide-speed"
                  type="checkbox"
                  checked={hideSpeed}
                  onChange={(event) => setHideSpeed(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'audio-player-dark',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="audio-player-dark"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, hideSkip, hideSpeed, hideVolume, highlightedState, src],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Audio player"
        description="Compact call-recording player from Messages Call logs: skip, play/pause, scrubber, volume, and speed."
        stageClassName="ui-preview__stage--wide audio-player-showcase__stage-wrap"
      >
        <div
          className={`audio-player-showcase__sheet${
            darkMode ? ' audio-player-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Matches the Call logs expanded-row player. Use a CORS-enabled
              audio URL for live playback.
            </p>
            <AudioPlayer
              src={src}
              darkMode={darkMode}
              hideSkip={hideSkip}
              hideVolume={hideVolume}
              hideSpeed={hideSpeed}
            />
          </section>

          <section className="audio-player-showcase__matrix">
            <h4 className="audio-player-showcase__section-title">States</h4>
            {AUDIO_PLAYER_PREVIEW_STATES.map((state) => (
              <div
                key={state}
                className={`audio-player-showcase__row${
                  state === highlightedState
                    ? ' audio-player-showcase__row--selected'
                    : ''
                }${darkMode ? ' audio-player-showcase__row--dark' : ''}`}
              >
                <span className="audio-player-showcase__row-label">
                  {AUDIO_PLAYER_PREVIEW_STATE_LABELS[state]}
                </span>
                <AudioPlayer previewState={state} darkMode={darkMode} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Audio player"
        sections={propertySections}
      />
    </>
  )
}

export default AudioPlayerShowcase
